"use client"

import { useState, useMemo, useEffect } from "react"
import { usePathname } from "next/navigation"
import { motion } from "framer-motion"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Search, Filter, Grid, List } from "lucide-react"
import { Button } from "@/components/ui/button"
import ModernProductCard from "@/components/modern-product-card"
import MobileProductCard from "@/components/mobile/mobile-product-card"
import TextReveal from "@/components/animated/text-reveal"
import GradientBackground from "@/components/animated/gradient-background"
import { products } from "@/data/products"
import Breadcrumbs from "@/components/seo/breadcrumbs"

interface Product {
  id: number
  name: string
  category: string | null
  subcategory: string | null
  image: string
  alt: string
  meta: {
    title: string
    description: string
    keywords: string
  }
}

export default function ProductsPageClient() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedSubcategory, setSelectedSubcategory] = useState("all")
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const shuffleArray = (array: Product[]): Product[] => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Lazy initializer — shuffles on first render (page refresh)
  const [shuffledProducts, setShuffledProducts] = useState<Product[]>(() =>
    shuffleArray((products as Product[]) || [])
  )
  const [isMobile, setIsMobile] = useState(false)
  const pathname = usePathname()

  // Re-shuffle whenever the user navigates to this page (client-side routing)
  useEffect(() => {
    setShuffledProducts(shuffleArray((products as Product[]) || []))
  }, [pathname])

  // Separate effect for mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  const allProducts = shuffledProducts


  const categories = useMemo(() => {
    const cats = [...new Set((products as Product[] || []).map((p) => p.category).filter((c): c is string => c !== null))]
    return cats.sort()
  }, [])

  const subcategories = useMemo(() => {
    if (selectedCategory === "all") return []
    const subcats = [
      ...new Set(
        allProducts
          .filter((p) => p.category === selectedCategory)
          .map((p) => p.subcategory)
          .filter((s): s is string => s !== null),
      ),
    ]
    return subcats.sort()
  }, [selectedCategory])

  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.meta.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.meta.keywords.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory
      const matchesSubcategory = selectedSubcategory === "all" || product.subcategory === selectedSubcategory

      return matchesSearch && matchesCategory && matchesSubcategory
    })
  }, [searchTerm, selectedCategory, selectedSubcategory])

  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-50 pb-20">
        <div className="px-4 py-6">
          {/* Header */}
          <TextReveal>
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-slate-900 mb-4">Our Products</h1>
              <p className="text-lg text-slate-600">Browse our comprehensive collection</p>
            </div>
          </TextReveal>

          <Breadcrumbs items={[{ name: "Products", url: "/products" }]} />

          {/* Mobile Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-4 mb-6 space-y-4"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-0 bg-slate-50 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value)
                  setSelectedSubcategory("all")
                }}
              >
                <SelectTrigger className="border-0 bg-slate-50 rounded-xl">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {subcategories.length > 0 && (
                <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                  <SelectTrigger className="border-0 bg-slate-50 rounded-xl">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {subcategories.map((subcategory) => (
                      <SelectItem key={subcategory} value={subcategory}>
                        {subcategory}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex items-center justify-center">
              <Badge variant="outline" className="bg-slate-50">
                <Filter className="h-3 w-3 mr-1" />
                {filteredProducts.length} products
              </Badge>
            </div>
          </motion.div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {filteredProducts.map((product, index) => (
                <MobileProductCard key={product.id} product={product} index={index} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-16"
            >
              <div className="text-slate-400 mb-4">
                <Search className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No products found</h3>
              <p className="text-slate-600">Try adjusting your search criteria</p>
            </motion.div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <TextReveal>
          <div className="mb-12 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Our Products</h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Browse our comprehensive collection of industrial hardware products
            </p>
          </div>
        </TextReveal>

        <Breadcrumbs items={[{ name: "Products", url: "/products" }]} />

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border-0"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-0 bg-slate-50/50 focus:bg-white transition-colors"
              />
            </div>

            <Select
              value={selectedCategory}
              onValueChange={(value) => {
                setSelectedCategory(value)
                setSelectedSubcategory("all")
              }}
            >
              <SelectTrigger className="border-0 bg-slate-50/50 focus:bg-white">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {subcategories.length > 0 && (
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                <SelectTrigger className="border-0 bg-slate-50/50 focus:bg-white">
                  <SelectValue placeholder="All Subcategories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subcategories</SelectItem>
                  {subcategories.map((subcategory) => (
                    <SelectItem key={subcategory} value={subcategory}>
                      {subcategory}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="flex items-center justify-between space-x-2">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <Badge variant="outline" className="bg-white/50">
                  {filteredProducts.length} products
                </Badge>
              </div>

              <div className="flex space-x-1">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="p-2"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="p-2"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className={`grid gap-8 ${viewMode === "grid"
              ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-1 md:grid-cols-2 gap-6"
              }`}
          >
            {filteredProducts.map((product, index) => (
              <ModernProductCard key={product.id} product={product} index={index} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-16"
          >
            <div className="text-slate-400 mb-6">
              <Search className="h-20 w-20 mx-auto" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-3">No products found</h3>
            <p className="text-slate-600 text-lg">Try adjusting your search criteria</p>
          </motion.div>
        )}
      </div>
    </div>
  )
}
